import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function sameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

function dateFromSelect(selected, selectedDay) {
  if (selectedDay) return selectedDay
  if (selected instanceof Date) return selected
  if (selected?.from) return selected.from
  if (selected?.to) return selected.to
  return null
}

/** Shared layout tokens — equal-width columns + full-width day cells — merged with consumer `classNames`. */
function Calendar({
  className,
  classNames: userClassNames,
  showOutsideDays = true,
  month: monthProp,
  defaultMonth,
  onMonthChange,
  onSelect,
  ...props
}) {
  const isMonthControlled = monthProp !== undefined
  const [internalMonth, setInternalMonth] = React.useState(() =>
    startOfMonth(monthProp || defaultMonth || new Date())
  )

  const visibleMonth = isMonthControlled ? startOfMonth(monthProp) : internalMonth

  const handleMonthChange = (nextMonth) => {
    const normalized = startOfMonth(nextMonth)
    if (!isMonthControlled) setInternalMonth(normalized)
    onMonthChange?.(normalized)
  }

  const handleSelect = (selected, selectedDay, ...args) => {
    onSelect?.(selected, selectedDay, ...args)
    const day = dateFromSelect(selected, selectedDay)
    if (!day) return
    const targetMonth = startOfMonth(day)
    if (!sameMonth(targetMonth, visibleMonth)) {
      handleMonthChange(targetMonth)
    }
  }

  const { cell: cellUserClassNames, day: dayUserClassNames, ...classNamesRest } =
    userClassNames || {};
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      month={visibleMonth}
      onMonthChange={handleMonthChange}
      onSelect={handleSelect}
      classNames={{
        months: "flex w-full flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4 w-full min-w-0",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "font-heading text-sm tracking-tight",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex w-full gap-1",
        head_cell:
          "flex flex-1 basis-0 min-w-0 items-center justify-center pb-2 text-[10px] font-normal uppercase tracking-widest text-[var(--t-meta)]",
        row: "mt-1 flex w-full gap-1",
        cell: cn(
          "relative flex flex-1 basis-0 min-w-0 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md",
          cellUserClassNames
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "flex min-h-9 w-full min-w-0 items-center justify-center rounded-md border border-transparent p-0 text-sm font-normal hover:border-[var(--t-border)] aria-selected:opacity-100",
          dayUserClassNames
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-[var(--t-accent)] text-white hover:bg-[var(--t-accent)] hover:text-white focus:bg-[var(--t-accent)] focus:text-white",
        day_today: "border border-[var(--t-accent)] text-[var(--t-accent)]",
        day_outside:
          "day-outside text-[var(--t-meta)] opacity-40 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNamesRest,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
      }}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
